import { css } from '@emotion/react';
import { t } from 'i18next';
import { NotificationErrorState } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  NotificationBadge,
  PrimaryButton,
  SkeletonLoader,
  Tabs,
  Text,
  Button,
} from '@frontend/design-system';
import { BusyBee } from './busy-bee';
import { useFirestoreDbQuery } from './firestore/use-firestore-init';
import { PopoverFilter } from './grouped-notifications/filter-chips/components/popOverFilter';
import { ForYouFilterType } from './grouped-notifications/filter-chips/constants';
import { getForYouFilters } from './grouped-notifications/filter-chips/filter-config';
import { GroupedNotificationList } from './grouped-notifications/index';
import { NotificationFilters, NotificationList } from './historical-notification-list';
import { NotificationEmptyStateGraphic } from './notification-empty-state';
import { NotificationEmptyStateVet } from './notification-empty-state-vet';
import { useNotificationContext } from './notification-provider';

const Error = () => {
  const { t } = useTranslation('notifications');
  const firestoreDbQuery = useFirestoreDbQuery();

  return (
    <div
      style={{
        height: '70vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <NotificationErrorState height={300} width={300} />
      <Text css={{ marginTop: theme.spacing(3), fontSize: theme.spacing(1.75) }}>
        {t(`Uh oh! There’s been an issue retrieving your notifications.`)}
      </Text>
      <Button
        css={{ marginTop: theme.spacing(2) }}
        trackingId={`notifications-page-retry-button`}
        onClick={firestoreDbQuery.refetchSignInToken}
      >
        {' '}
        <Icon name='update-small' />
        Retry
      </Button>
    </div>
  );
};

const EmptyState = () => {
  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { t } = useTranslation('notifications');

  return (
    <div css={{ margin: theme.spacing(3), p: { color: theme.colors.neutral70 } }}>
      {lastUsedVertical === 'VET' ? (
        <NotificationEmptyStateVet css={{ margin: theme.spacing(4) }} />
      ) : (
        <NotificationEmptyStateGraphic />
      )}
      <Text textAlign='center'>{t('Kick back and relax!')}</Text>
      <Text textAlign='center'>{t("You're all caught up on notifications.")}</Text>
    </div>
  );
};

const NotificationsDisabled = ({
  selectLocationTray,
}: {
  selectLocationTray: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const { t } = useTranslation('notifications');
  return (
    <div style={{ margin: theme.spacing(3), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <BusyBee css={{ margin: theme.spacing(5) }} />
      <BannerNotification
        status='info'
        message={
          <>
            <Text size='medium'>
              {t(
                `You're a busy bee. Unfortunately, after 30+ locations are selected, our hive is full with too much buzzing.`
              )}
            </Text>
            <Text weight='bold' size='medium'>
              {t('Deselect some locations to resume notifications.')}
            </Text>
          </>
        }
      />
      <PrimaryButton css={{ width: 'auto', margin: theme.spacing(4) }} onClick={(e) => selectLocationTray(e)}>
        {t('Edit Location Selection')}
      </PrimaryButton>
    </div>
  );
};

export const NotificationLoaderList = ({ length = 5 }: { length?: number }) => (
  // 66 is not an arbitrary number, it is the amount of spacing about the notification list when list items are rendered
  // it is used here so that there is not a layout shift when the skeleton loader is replaced with the actual content
  <div css={{ marginTop: 66 }}>
    {Array.from({ length }).map((_, index) => (
      <div key={index} css={{ display: 'flex', margin: theme.spacing(3, 1) }}>
        <SkeletonLoader css={{ margin: theme.spacing(2) }} height={30} width={30} />
        <div>
          <SkeletonLoader height={14} width={100} css={{ marginBottom: theme.spacing(1) }} />
          <SkeletonLoader height={20} width={150} css={{ marginBottom: theme.spacing(1) }} />
          <SkeletonLoader height={14} width={80} />
        </div>
        <SkeletonLoader height={14} width={100} css={{ marginLeft: 'auto', marginRight: theme.spacing(2) }} />
      </div>
    ))}
  </div>
);

export const NotificationTray = ({
  closeModal,
  selectLocationTray,
}: {
  closeModal: () => void;
  selectLocationTray: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const firestoreDbQuery = useFirestoreDbQuery();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        height: showForYouNotificationsTab ? 'max-content' : '100%',
        position: 'relative',
      }}
    >
      {((firestoreDbQuery.db === null && !firestoreDbQuery.queryData.isLoading) ||
        firestoreDbQuery.queryData.isError) && <Error />}
      {firestoreDbQuery.queryData.isLoading && <NotificationLoaderList />}
      {firestoreDbQuery.queryData.isSuccess && firestoreDbQuery.db && (
        <Notifications closeModal={closeModal} selectLocationTray={selectLocationTray} />
      )}
    </section>
  );
};

const Notifications = ({
  closeModal,
  selectLocationTray,
}: {
  closeModal: () => void;
  selectLocationTray: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const ctx = useNotificationContext();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const showGroupedForYouNotifications = getFeatureFlagValue('grouping-for-you-notifications');
  const showColorChipForYou = getFeatureFlagValue('for-you-colors-chip');

  const popOverFilters = getForYouFilters(showColorChipForYou).map((filter: ForYouFilterType) => {
    return <PopoverFilter filter={filter} />;
  });

  if (ctx.maxLocationsSelected) {
    return <NotificationsDisabled selectLocationTray={selectLocationTray} />;
  }
  if (ctx.firebaseError) {
    return <Error />;
  }
  if (ctx.notifications === null) {
    return <NotificationLoaderList />;
  }
  if (showForYouNotificationsTab) {
    return (
      <Tabs initialTab='forYou'>
        <div css={{ position: 'sticky', top: '0', background: theme.colors.white, zIndex: 3 }}>
          <Tabs.Bar fullWidth={false} style={{ padding: `0 ${theme.spacing(2)}`, marginTop: theme.spacing(2) }}>
            <Tabs.Tab id='forYou' trackingId='for-you-notification-tab'>
              <Text
                as={'span'}
                css={css`
                  display: flex;
                  align-items: center;
                  font-size: ${theme.spacing(1.75)};
                  gap: ${theme.spacing(1)};
                `}
              >
                {t('For You')}{' '}
                {!!ctx.unreadForYouCount && (
                  <NotificationBadge>
                    {showGroupedForYouNotifications ? ctx.unreadGroupedForYouCount : ctx.unreadForYouCount}
                  </NotificationBadge>
                )}
              </Text>
            </Tabs.Tab>
            <Tabs.Tab id='timeline' trackingId='timeline-notification-tab'>
              {t('Timeline')}
            </Tabs.Tab>
          </Tabs.Bar>
        </div>
        <Tabs.Panel controller='forYou'>
          {(() => {
            const noNotifications =
              ctx.forYouNotifications?.length === 0 ||
              (showGroupedForYouNotifications && Object.keys(ctx.forYouGroupedNotifications || {}).length === 0);

            if (noNotifications) {
              return (
                <>
                  {showGroupedForYouNotifications && (
                    <div
                      css={{
                        display: 'flex',
                        gap: theme.spacing(1),
                        margin: `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(3)}`,
                      }}
                    >
                      {popOverFilters}
                    </div>
                  )}
                  <EmptyState />
                </>
              );
            }

            if (showGroupedForYouNotifications) {
              return (
                <GroupedNotificationList
                  groupedNotifications={ctx.forYouGroupedNotifications || {}}
                  closeModal={closeModal}
                  setGroupNotifications={ctx.setGroupedForYouData}
                />
              );
            }

            return <NotificationList notifications={ctx.forYouNotifications || []} closeModal={closeModal} />;
          })()}
        </Tabs.Panel>

        <Tabs.Panel controller='timeline'>
          <NotificationFilters />
          {ctx.notifications?.length === 0 ? (
            <EmptyState />
          ) : (
            <NotificationList notifications={ctx.notifications} closeModal={closeModal} />
          )}
        </Tabs.Panel>
      </Tabs>
    );
  }

  if (ctx.notifications?.length === 0) {
    return <EmptyState />;
  }
  return <NotificationList notifications={ctx.notifications} closeModal={closeModal} />;
};
